import type { ReactNode } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import type { Icons } from '@assets/index';
import Icon from '@components/common/Icon';
import { radixTooltipContentClass } from '@components/common/radixTooltipContentClass';

export type TableRowIconAction = {
    icon: keyof typeof Icons;
    label: string;
    onClick: () => void;
    variant?: 'default' | 'danger';
    disabled?: boolean;
};

const btnBase =
    'inline-flex items-center justify-center shrink-0 size-9 rounded-lg transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 disabled:opacity-40 disabled:pointer-events-none';

const btnDefault = `${btnBase} hover:bg-[var(--primary-surface-color)] focus-visible:outline-[var(--primary-color)]`;

const btnDanger = `${btnBase} hover:bg-[var(--error-canvas-color)] focus-visible:outline-[var(--semantic-error-color)]`;

const iconClassName =
    'w-[18px] h-[18px] min-w-[18px] min-h-[18px] object-contain';

const ICON_SIZE = 20;

/**
 * Wrap listing pages that use `TableRowIconActions` so Radix tooltips work (matches sidebar / audits delay).
 */
export function TableRowActionsTooltipProvider({ children }: { children: ReactNode }) {
    return (
        <Tooltip.Provider delayDuration={250} skipDelayDuration={200}>
            {children}
        </Tooltip.Provider>
    );
}

/**
 * Inline row actions: icon buttons + tooltips (same visual language as Audits list).
 * Must be used under `TableRowActionsTooltipProvider` (or another `Tooltip.Provider`).
 */
export function TableRowIconActions({ actions }: { actions: TableRowIconAction[] }) {
    if (actions.length === 0) return null;

    return (
        <div className="flex items-center justify-center gap-1 2xl:gap-1.5 min-w-0">
            {actions.map((action, index) => (
                <Tooltip.Root key={`${action.label}-${index}`}>
                    <Tooltip.Trigger asChild>
                        <button
                            type="button"
                            className={action.variant === 'danger' ? btnDanger : btnDefault}
                            aria-label={action.label}
                            disabled={action.disabled}
                            onClick={action.onClick}
                        >
                            <Icon name={action.icon} size={ICON_SIZE} className={iconClassName} alt="" />
                        </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content
                            side="top"
                            align="center"
                            sideOffset={8}
                            collisionPadding={8}
                            className={radixTooltipContentClass}
                        >
                            {action.label}
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root>
            ))}
        </div>
    );
}
